import {isSubset, naturalSort} from '@benchling/js-utils';

import {TaskSchemaNodeSectionType} from '../../processFlowchartConfigEditorConstants';
import {Transition} from './TransitionTypes';

export function getTransitionId(sourcePTDVIds: Array<string>, targetPTDVId: string): string {
  const sortedSources = naturalSort([...sourcePTDVIds]);
  return `transition:${sortedSources.join('+')}:${targetPTDVId}`;
}

export function getPTDVPairKey(sourcePTDVId: string, targetPTDVId: string): string {
  return `${sourcePTDVId}:${targetPTDVId}`;
}

export function getNodePairKey(sourceNodeId: string, targetNodeId: string): string {
  return `${sourceNodeId}:${targetNodeId}`;
}

export interface FieldPairKeyParts {
  sourcePTDVId: string;
  sourceSectionType: TaskSchemaNodeSectionType;
  sourceFieldClientId: string;
  targetPTDVId: string;
  targetSectionType: TaskSchemaNodeSectionType;
  targetFieldClientId: string;
}

export function parseFieldPairKey(key: string): FieldPairKeyParts | null {
  const parts = key.split(':');
  if (parts.length !== 6) {
    return null;
  }
  const [
    sourcePTDVId,
    sourceSectionType,
    sourceFieldClientId,
    targetPTDVId,
    targetSectionType,
    targetFieldClientId,
  ] = parts;
  return {
    sourcePTDVId,
    sourceSectionType: sourceSectionType as TaskSchemaNodeSectionType,
    sourceFieldClientId,
    targetPTDVId,
    targetSectionType: targetSectionType as TaskSchemaNodeSectionType,
    targetFieldClientId,
  };
}

/** Generate field pair key, for field to field mappings. */
export function getFieldPairKey({
  sourcePTDVId,
  sourceSectionType,
  sourceFieldClientId,
  targetPTDVId,
  targetSectionType,
  targetFieldClientId,
}: FieldPairKeyParts): string {
  return `${sourcePTDVId}:${sourceSectionType}:${sourceFieldClientId}:${targetPTDVId}:${targetSectionType}:${targetFieldClientId}`;
}

/** Build index of transition IDs by source + target PTDV pair (each pair maps to an array of transition IDs, of transitions that use that pair) */
export function buildTransitionIndexByPTDVPair(
  transitions: Record<string, Transition>
): Record<string, Array<string>> {
  return buildTransitionIndexByPTDVPairWithSubsets(transitions).index;
}

export interface BuildTransitionIndexByPTDVPairWithSubsetDetectionResult {
  index: Record<string, Array<string>>;
  /** Transition IDs whose source set is a proper subset of another transition's source set for the same target */
  subsetTransitionIds: Array<string>;
  /** Transition IDs whose source set is a proper superset of another transition's source set for the same target */
  supersetTransitionIds: Array<string>;
}

/**
 * Build index of transition IDs by source + target PTDV pair and also detects when a transition's
 * source set is a subset of another's source set for the same target.
 * Each pair key maps to an array of all transition IDs that use that pair.
 */
export function buildTransitionIndexByPTDVPairWithSubsets(
  transitions: Record<string, Transition>
): BuildTransitionIndexByPTDVPairWithSubsetDetectionResult {
  const index: Record<string, Array<string>> = {};
  const subsetTransitionIds = new Set<string>();
  const supersetTransitionIds = new Set<string>();

  for (const transition of Object.values(transitions)) {
    for (const sourcePTDVId of transition.sourcePTDVIds) {
      const pairKey = getPTDVPairKey(sourcePTDVId, transition.targetPTDVId);
      const existingIds = index[pairKey] ?? [];

      for (const existingId of existingIds) {
        if (existingId === transition.id) {
          continue;
        }
        const existingTransition = transitions[existingId];
        if (existingTransition != null) {
          const currentSet = new Set(transition.sourcePTDVIds);
          const existingSet = new Set(existingTransition.sourcePTDVIds);
          const currentIsProperSubsetOfExisting =
            isSubset(currentSet, existingSet) && currentSet.size < existingSet.size;
          const existingIsProperSubsetOfCurrent =
            isSubset(existingSet, currentSet) && existingSet.size < currentSet.size;
          if (currentIsProperSubsetOfExisting) {
            subsetTransitionIds.add(transition.id);
            supersetTransitionIds.add(existingId);
          } else if (existingIsProperSubsetOfCurrent) {
            subsetTransitionIds.add(existingId);
            supersetTransitionIds.add(transition.id);
          }
        }
      }

      if (!index[pairKey]) {
        index[pairKey] = [];
      }
      index[pairKey].push(transition.id);
    }
  }

  return {
    index,
    subsetTransitionIds: [...subsetTransitionIds],
    supersetTransitionIds: [...supersetTransitionIds],
  };
}

export interface CreateEmptyTransitionParams {
  sourcePTDVIds: Array<string>;
  targetPTDVId: string;
  isPlanned?: boolean;
}

export function createEmptyTransition({
  sourcePTDVIds,
  targetPTDVId,
  isPlanned = true,
}: CreateEmptyTransitionParams): Transition {
  return {
    id: getTransitionId(sourcePTDVIds, targetPTDVId),
    sourcePTDVIds,
    targetPTDVId,
    transitionSourcesBySourcePTDVId: {},
    customMappingDataByFieldPair: {},
    isPlanned,
  };
}

export interface GetOrCreateTransitionParams {
  sourcePTDVIds: Array<string>;
  targetPTDVId: string;
  transitions: Record<string, Transition>;
  isPlanned?: boolean;
}

export interface GetOrCreateTransitionResult {
  transition: Transition;
  isNew: boolean;
}

export function getOrCreateTransition({
  sourcePTDVIds,
  targetPTDVId,
  transitions,
  isPlanned = false,
}: GetOrCreateTransitionParams): GetOrCreateTransitionResult {
  const transitionId = getTransitionId(sourcePTDVIds, targetPTDVId);
  const existing = transitions[transitionId];

  if (existing) {
    return {transition: existing, isNew: false};
  }

  const newTransition = createEmptyTransition({
    sourcePTDVIds,
    targetPTDVId,
    isPlanned,
  });
  transitions[transitionId] = newTransition;
  return {transition: newTransition, isNew: true};
}
